import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Clock, Shield, CheckCircle, Save, Smartphone, X, MapPin, AlertCircle, Calendar } from 'lucide-react';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, limit, increment } from 'firebase/firestore';

// Firebase/Auth
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';

// Cart
import { useCart } from '../contexts/CartContext';
import type { CartItem } from '../contexts/CartContext';

// Components
import { OrderConfirmation } from '../components/OrderConfirmation';

// Email Service
import { sendOrderEmails } from '../utils/emailService';

// PhonePe Payment Service
import PhonePeService from '../utils/phonepeService';

// Order ID Generator
import { generateSequentialOrderId } from '../utils/orderIdGenerator';

// Rate Limiter
import { checkRateLimit, getDeviceFingerprint } from '../utils/rateLimiter';

// Analytics
import { trackBeginCheckout, trackCheckoutStep, trackPurchase, trackPromoCode } from '../utils/analytics';

// Service Task Manager
import { createServiceTasksFromOrder } from '../utils/serviceTaskManager';

// Basic types
type PaymentMethod = 'card' | 'upi' | 'pas' | 'phonepe';
type Step = 1 | 2 | 3;

// Base user information
interface UserDetails {
  fullName: string;
  phone: string;
  email: string;
  houseNumber: string;
  landmark: string;
  streetAddress: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  address: string;
  // Optional saved payment info
  cardNumber?: string;
  cardExpiry?: string;
  cardholderName?: string;
}

// Complete form data for checkout
export interface FormData {
  // User Details
  fullName: string;
  phone: string;
  email: string;
  houseNumber: string;
  landmark: string;
  streetAddress: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  address: string;
  
  // Payment Details
  paymentMethod: PaymentMethod;
  cardNumber: string;
  cardExpiry: string;
  cardholderName: string;
  expiryDate: string;
  cvv: string;
  
  // Scheduling Details
  preferredDate: string;
  preferredTime: string;
}

// Additional service add-on
interface AddOn {
  id: string;
  name: string;
  price: number;
  duration?: string;
}

// Extended Cart Item with scheduling and payment for each item
interface OrderItem extends CartItem {
  scheduling: {
    date: string;
    time: string;
  };
  payment: {
    method: PaymentMethod;
    status: 'pending' | 'completed' | 'failed';
    subtotal: number;
    total: number;
  };
  status: 'placed' | 'confirmed' | 'active' | 'completed' | 'cancelled';
  providerId?: string;
  providerName?: string;
  providerPhone?: string;
  confirmedAt?: string;
  startedAt?: string;
  completedAt?: string;
  cancelledAt?: string;
}

// Order data structure
interface OrderData {
  userId: string;
  items: OrderItem[]; // Each item has its own scheduling and payment
  tags?: string[]; // Service categories/tags extracted from items
  address: Omit<UserDetails, 'preferredDate' | 'preferredTime'>;
  status: 'placed' | 'cancelled' | 'completed';
  createdAt: string;
}

// Scheduling data type
interface SchedulingFields {
  preferredDate: string;
  preferredTime: string;
}

// Toast notification component
const Toast = ({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg ${
        type === 'success' ? 'bg-green-500' : 'bg-red-500'
      } text-white z-50`}
    >
      {message}
    </motion.div>
  );
};

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
  </div>
);

// Helper functions
const calculateTotals = (cartItems: CartItem[]) => {
  const subtotal = cartItems.reduce((sum: number, item: CartItem) => {
    // Calculate base price
    let itemTotal = item.price * item.quantity;
    
    // Add addon prices if any
    if (item.addons && item.addons.length > 0) {
      const addonTotal = item.addons.reduce((addonSum: number, addon: AddOn) => addonSum + addon.price, 0);
      itemTotal += addonTotal * item.quantity;
    }
    
    return sum + itemTotal;
  }, 0);
  
  const total = subtotal;
  return { subtotal, total };
};

// Main component
function CheckoutPage() {
  // Hooks
  const auth = useAuth();
  const cart = useCart();
  const navigate = useNavigate();

  // State declarations
  const [step, setStep] = useState<Step>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<{ code: string; discount: number; type?: string } | null>(null);
  const [promoError, setPromoError] = useState('');
  const [validPromoCode, setValidPromoCode] = useState<any>(null);
  
  // Form data state
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    phone: '',
    email: '',
    houseNumber: '',
    landmark: '',
    streetAddress: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    address: '',
    paymentMethod: 'card',
    cardNumber: '',
    cardExpiry: '',
    cardholderName: '',
    expiryDate: '',
    cvv: '',
    preferredDate: '',
    preferredTime: '',
  });

  // Prevent accidental navigation away from checkout only for PhonePe payment gateway
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only show warning when navigating to PhonePe payment
      if (formData.paymentMethod === 'phonepe' && orderPlaced && !window.location.href.includes('phonepe')) {
        e.preventDefault();
        e.returnValue = 'Payment is being processed. Please wait...';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [orderPlaced, formData.paymentMethod]);

  // Calculate order totals
  const totalsData = calculateTotals(cart.cartItems);
  let subtotal = totalsData.subtotal;
  let discount = 0;
  
  // Apply promo code discount
  if (appliedPromo) {
    if (appliedPromo.type === 'percentage') {
      // Percentage discount
      discount = Math.round(subtotal * (appliedPromo.discount / 100));
    } else {
      // Flat discount
      discount = appliedPromo.discount;
    }
    // Ensure discount doesn't exceed subtotal
    discount = Math.min(discount, subtotal);
  }
  
  const total = subtotal - discount;

  // Fetch user details from Firestore on component mount
  useEffect(() => {
    const fetchUserDetails = async () => {
      // Wait for auth to finish loading
      if (auth.loading) {
        return;
      }

      // Redirect to login if not authenticated
      if (!auth.user) {
        navigate('/login');
        return;
      }

      try {
        setIsLoading(true);
        const userDocRef = doc(db, 'users', auth.user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('Fetched user data from Firestore:', userData);
          
          // Find default address from addresses array
          let defaultAddress = null;
          if (userData.addresses && userData.addresses.length > 0) {
            // Try to find address marked as default
            defaultAddress = userData.addresses.find((addr: any) => addr.isDefault) 
              || userData.addresses.find((addr: any) => addr.id === userData.defaultAddressId)
              || userData.addresses[0]; // Fallback to first address
            console.log('Default address found:', defaultAddress);
          }
          
          setFormData(prev => ({
            ...prev,
            fullName: defaultAddress?.fullName || userData.fullName || userData.displayName || '',
            phone: defaultAddress?.phone || userData.phoneNumber || userData.phone || '',
            email: userData.email || auth.user?.email || '',
            houseNumber: defaultAddress?.houseNumber || userData.houseNumber || '',
            landmark: defaultAddress?.landmark || userData.landmark || '',
            streetAddress: defaultAddress?.streetAddress || userData.streetAddress || '',
            city: defaultAddress?.city || userData.city || '',
            state: defaultAddress?.state || userData.state || 'Himachal Pradesh',
            country: defaultAddress?.country || userData.country || 'India',
            pincode: defaultAddress?.pincode || userData.pincode || '',
            address: defaultAddress?.address || userData.address || '',
          }));
        } else {
          console.log('User document does not exist in Firestore');
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        showToastMessage('Failed to load user details', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, [auth.user, auth.loading, navigate]);

  // Track checkout initiation
  useEffect(() => {
    if (cart.cartItems.length > 0 && step === 1) {
      trackBeginCheckout(subtotal, cart.cartItems.length);
    }
  }, []); // Only on mount

  // Track checkout step progression
  useEffect(() => {
    const stepNames = {
      1: 'Shipping Information',
      2: 'Date & Time Selection',
      3: 'Payment Details'
    };
    if (stepNames[step]) {
      trackCheckoutStep(step, stepNames[step]);
    }
  }, [step]);

  // Populate date and time from cart items (on initial load and when cart changes)
  useEffect(() => {
    console.log('📅 [SCHEDULING] Step:', step, 'Cart items:', cart.cartItems.length);
    
    if (cart.cartItems && cart.cartItems.length > 0) {
      const firstItem = cart.cartItems[0];
      console.log('📅 [SCHEDULING] Cart item date/time:', {
        date: firstItem.date,
        time: firstItem.time
      });
      
      // Always populate from cart if cart has date/time
      if (firstItem.date || firstItem.time) {
        setFormData(prev => {
          const newDate = firstItem.date || prev.preferredDate || '';
          const newTime = firstItem.time || prev.preferredTime || '';
          
          console.log('✅ [SCHEDULING] Setting:', {
            from: { date: prev.preferredDate, time: prev.preferredTime },
            to: { date: newDate, time: newTime }
          });
          
          return {
            ...prev,
            preferredDate: newDate,
            preferredTime: newTime,
          };
        });
      } else {
        console.log('⚠️ [SCHEDULING] No date/time in cart item');
      }
    } else {
      console.log('⚠️ [SCHEDULING] No cart items found');
    }
  }, [cart.cartItems, step]);

  // Auto-scroll when step changes
  useEffect(() => {
    if (step === 2) {
      // Scroll to date/time section on step 2
      setTimeout(() => {
        const dateTimeSection = document.querySelector('[name="preferredDate"]');
        if (dateTimeSection) {
          dateTimeSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else if (step === 3) {
      // Scroll to payment section on step 3
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [step]);

  // Show toast message helper
  const showToastMessage = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  // Handle promo code application - database-driven
  const handleApplyPromo = async () => {
    const code = promoCode.toUpperCase().trim();
    
    if (!code) {
      setPromoError('Please enter a promo code');
      return;
    }
    
    // Rate limiting check for promo code validation
    const userId = auth.user?.uid || getDeviceFingerprint();
    const rateLimitCheck = checkRateLimit(userId, 'PROMO_CODE_CHECK');
    if (!rateLimitCheck.allowed) {
      setPromoError(rateLimitCheck.message || 'Too many attempts');
      showToastMessage(rateLimitCheck.message || 'Too many promo code attempts', 'error');
      return;
    }
    
    // Calculate current subtotal for validation
    const currentSubtotal = calculateTotals(cart.cartItems).subtotal;
    
    try {
      // Query Firestore for the promo code
      const promoQuery = query(
        collection(db, 'promoCodes'),
        where('code', '==', code),
        where('isActive', '==', true)
      );
      
      const promoSnapshot = await getDocs(promoQuery);
      
      if (promoSnapshot.empty) {
        // Check if code exists but is inactive
        const inactiveQuery = query(
          collection(db, 'promoCodes'),
          where('code', '==', code)
        );
        const inactiveSnapshot = await getDocs(inactiveQuery);
        
        if (!inactiveSnapshot.empty) {
          setPromoError('This promo code is currently inactive');
          showToastMessage('Promo code is inactive', 'error');
        } else {
          setPromoError('Invalid or expired promo code');
          showToastMessage('Invalid promo code', 'error');
        }
        return;
      }
      
      const promoDoc = promoSnapshot.docs[0];
      const promoData = promoDoc.data();
      
      // Special handling for FIRST20 promo code
      if (code === 'FIRST20') {
        // Check if user has already used this promo code
        if (auth.user?.uid) {
          const userOrdersQuery = query(
            collection(db, 'orders'),
            where('userId', '==', auth.user.uid),
            where('promoCode', '==', 'FIRST20')
          );
          const userOrdersSnapshot = await getDocs(userOrdersQuery);
          
          if (!userOrdersSnapshot.empty) {
            setPromoError('You have already used this promo code');
            showToastMessage('FIRST20 can only be used once per user', 'error');
            return;
          }
        }
        
        // Check total usage limit (20 times)
        const totalUsageQuery = query(
          collection(db, 'orders'),
          where('promoCode', '==', 'FIRST20')
        );
        const totalUsageSnapshot = await getDocs(totalUsageQuery);
        
        if (totalUsageSnapshot.size >= 20) {
          setPromoError('This promo code has reached its maximum usage limit');
          showToastMessage('FIRST20 promotion has ended', 'error');
          return;
        }
      }
      
      // Check if promo code has expired
      const now = new Date();
      const expiryDate = promoData.expiryDate ? new Date(promoData.expiryDate) : null;
      
      if (expiryDate && expiryDate < now) {
        setPromoError('This promo code has expired');
        showToastMessage('Promo code expired', 'error');
        return;
      }
      
      // Check usage limit
      if (promoData.usageLimit && promoData.usedCount >= promoData.usageLimit) {
        setPromoError('This promo code has reached its usage limit');
        showToastMessage('Promo code limit reached', 'error');
        return;
      }
      
      // Check minimum order value
      if (promoData.minOrderValue && currentSubtotal < promoData.minOrderValue) {
        setPromoError(`Minimum order value ₹${promoData.minOrderValue} required`);
        showToastMessage(`Minimum order ₹${promoData.minOrderValue} required`, 'error');
        return;
      }
      
      // Apply promo code
      setValidPromoCode({ id: promoDoc.id, ...promoData });
      setAppliedPromo({ 
        code, 
        discount: promoData.discountValue,
        type: promoData.discountType 
      });
      setPromoError('');
      
      // Calculate discount amount for tracking
      const discountAmount = promoData.discountType === 'percentage' 
        ? Math.round(currentSubtotal * (promoData.discountValue / 100))
        : promoData.discountValue;
      
      // Track successful promo code application
      trackPromoCode(code, true, discountAmount);
      
      showToastMessage(`Promo code "${code}" applied! Save ₹${promoData.discountType === 'percentage' ? `${promoData.discountValue}%` : promoData.discountValue}`, 'success');
    } catch (error) {
      console.error('Error applying promo code:', error);
      setPromoError('Failed to apply promo code');
      showToastMessage('Error applying promo code', 'error');
      
      // Track failed promo code attempt
      trackPromoCode(code, false);
    }
  };

  // Handle promo code removal
  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoError('');
    showToastMessage('Promo code removed', 'success');
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate step 1 (Address Details)
  const validateStep1 = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.fullName.trim()) errors.fullName = 'Full name is required';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.houseNumber.trim()) errors.houseNumber = 'House number is required';
    if (!formData.streetAddress.trim()) errors.streetAddress = 'Street address is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.state.trim()) errors.state = 'State is required';
    if (!formData.pincode.trim()) errors.pincode = 'Pincode is required';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate step 2 (Date & Time) - Now just checks if cart items have dates
  const validateStep2 = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Check if all cart items have dates and times
    const itemsWithoutSchedule = cart.cartItems.filter(item => !item.date || !item.time);
    if (itemsWithoutSchedule.length > 0) {
      errors.preferredDate = 'All services must have scheduled dates and times';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate step 3 (Payment)
  const validateStep3 = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Card payment requires card details
    if (formData.paymentMethod === 'card') {
      if (!formData.cardNumber.trim()) errors.cardNumber = 'Card number is required';
      if (!formData.cardholderName.trim()) errors.cardholderName = 'Cardholder name is required';
      if (!formData.expiryDate.trim()) errors.expiryDate = 'Expiry date is required';
      if (!formData.cvv.trim()) errors.cvv = 'CVV is required';
    }
    
    // PhonePe, UPI, and COD don't require additional validation

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle next step
  const handleNextStep = async () => {
    let isValid = false;

    if (step === 1) {
      isValid = validateStep1();
    } else if (step === 2) {
      isValid = validateStep2();
    }

    if (isValid) {
      if (step < 3) {
        setStep((prev) => (prev + 1) as Step);
      }
    } else {
      showToastMessage('Please fill in all required fields', 'error');
    }
  };

  // Handle previous step
  const handlePrevStep = () => {
    if (step > 1) {
      setStep((prev) => (prev - 1) as Step);
    }
  };

  // Handle PhonePe payment
  const handlePhonePePayment = async () => {
    if (!auth.user) return;

    try {
      setIsProcessingPayment(true);
      showToastMessage('Initiating PhonePe payment...', 'success');

      // Generate sequential order ID
      const orderId = await generateSequentialOrderId();
      console.log('Generated sequential order ID:', orderId);
      
      // Extract unique categories/tags from cart items
      const tags = Array.from(new Set(
        cart.cartItems
          .map(item => item.category)
          .filter(Boolean)
      )) as string[];

      // Extract service names for provider matching (IMPORTANT)
      const serviceNames = Array.from(new Set(
        cart.cartItems
          .map(item => item.name)
          .filter(Boolean)
      )) as string[];

      // Transform cart items to include scheduling and payment for each item
      const phonepeOrderItems: OrderItem[] = cart.cartItems.map(item => {
        let itemSubtotal = item.price * item.quantity;
        if (item.addons && item.addons.length > 0) {
          const addonTotal = item.addons.reduce((sum, addon) => sum + addon.price, 0);
          itemSubtotal += addonTotal * item.quantity;
        }

        let itemTotal = itemSubtotal;
        if (appliedPromo && discount > 0) {
          const discountRatio = discount / subtotal;
          const itemDiscount = itemSubtotal * discountRatio;
          itemTotal = itemSubtotal - itemDiscount;
        }

        return {
          ...item,
          scheduling: {
            date: item.date,
            time: item.time,
          },
          payment: {
            method: 'phonepe',
            status: 'pending',
            subtotal: itemSubtotal,
            total: itemTotal,
          },
          status: 'placed',
        };
      });

      // Prepare order data with unified structure
      const phonepeOrderData: any = {
        userId: auth.user.uid,
        userInfo: auth.user.uid, // For legacy compatibility
        items: phonepeOrderItems,
        serviceNames, // For provider matching
        ...(tags.length > 0 && { tags }), // Only include tags field if tags exist
        address: {
          fullName: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          houseNumber: formData.houseNumber || '',
          landmark: formData.landmark || '',
          streetAddress: formData.streetAddress || '',
          city: formData.city || '',
          state: formData.state || '',
          country: formData.country || 'India',
          pincode: formData.pincode || '',
          address: formData.address || '',
        },
        // Scheduling - first item's schedule as primary
        scheduling: {
          date: phonepeOrderItems[0]?.scheduling?.date || '',
          time: phonepeOrderItems[0]?.scheduling?.time || '',
        },
        payment: {
          method: 'phonepe',
          status: 'pending', // Will be updated to 'completed' after payment verification
          amount: total,
          subtotal,
          total,
          transactionId: null,
          paidAt: null,
          ...(appliedPromo && {
            promoCode: appliedPromo.code,
            discount: discount
          })
        },
        // Additional services tracking (empty initially)
        additionalServices: [],
        // Price breakdown
        subtotal: subtotal,
        discount: discount,
        total: total,
        additionalTotal: 0,
        grandTotal: total,
        amountPaid: 0, // Will be updated to total after PhonePe success
        amountDue: total, // Will be updated to 0 after PhonePe success
        // Provider fields (will be set when provider accepts)
        providerId: null,
        providerName: null,
        providerPhone: null,
        // Timestamps
        confirmedAt: null,
        startedAt: null,
        completedAt: null,
        cancelledAt: null,
        status: 'pending', // Will be updated to 'placed' after payment success
        createdAt: new Date().toISOString(),
      };

      // Save order to Firestore BEFORE redirecting to PhonePe
      console.log('Creating PhonePe order in Firestore...');
      const orderRef = doc(db, 'orders', orderId);
      await setDoc(orderRef, phonepeOrderData);
      console.log('✅ PhonePe order created with ID:', orderId);

      // Initiate PhonePe payment
      const paymentResponse = await PhonePeService.initiatePayment({
        orderId,
        amount: total,
        customerName: formData.fullName,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        userId: auth.user.uid,
        orderData: phonepeOrderData, // Send full order data
      });

      if (paymentResponse.success) {
        const redirectUrl = PhonePeService.getRedirectUrl(paymentResponse);
        
        if (redirectUrl) {
          // Save minimal order reference to localStorage before redirect
          // Full order data is already saved in Firestore above
          const orderData: any = {
            userId: auth.user.uid,
            items: phonepeOrderItems,
            address: {
              fullName: formData.fullName,
              phone: formData.phone,
              email: formData.email,
              houseNumber: formData.houseNumber || '',
              landmark: formData.landmark || '',
              streetAddress: formData.streetAddress || '',
              city: formData.city || '',
              state: formData.state || '',
              country: formData.country || 'India',
              pincode: formData.pincode || '',
              address: formData.address || '',
            },
            createdAt: new Date().toISOString(),
          };
          
          // Store both order data and orderId
          localStorage.setItem('pendingOrderData', JSON.stringify(orderData));
          localStorage.setItem('pendingOrderId', orderId);
          
          console.log('PhonePe: Stored orderId in localStorage:', orderId);
          
          // Redirect to PhonePe payment page
          window.location.href = redirectUrl;
        } else {
          throw new Error('Payment redirect URL not available');
        }
      } else {
        throw new Error(paymentResponse.message || 'Payment initiation failed');
      }
    } catch (error: any) {
      console.error('PhonePe Payment Error:', error);
      showToastMessage(error.message || 'Failed to initiate payment', 'error');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Handle order placement
  const handlePlaceOrder = async () => {
    if (!validateStep3()) {
      showToastMessage('Please fill in all payment details', 'error');
      return;
    }

    if (!auth.user) {
      showToastMessage('Please login to place order', 'error');
      navigate('/login');
      return;
    }

    // Rate limiting check
    const rateLimitCheck = checkRateLimit(auth.user.uid, 'ORDER_PLACEMENT');
    if (!rateLimitCheck.allowed) {
      showToastMessage(rateLimitCheck.message || 'Too many order attempts. Please wait.', 'error');
      return;
    }

    // If PhonePe payment, initiate payment first
    // Note: PhonePe Cloud Function will create the order, so we don't create it here
    if (formData.paymentMethod === 'phonepe') {
      await handlePhonePePayment();
      return;
    }

    // For COD and other payment methods, continue with regular order placement
    try {
      setIsSaving(true);

      // Log form data for debugging
      console.log('Form Data:', formData);
      console.log('Cart Items:', cart.cartItems);
      console.log('User:', auth.user);

      // Validate required fields
      if (!formData.fullName || !formData.phone || !formData.email) {
        showToastMessage('Missing required user information', 'error');
        return;
      }

      // Validate all cart items have dates and times
      const itemsWithoutSchedule = cart.cartItems.filter(item => !item.date || !item.time);
      if (itemsWithoutSchedule.length > 0) {
        showToastMessage('All services must have scheduled dates and times', 'error');
        return;
      }

      if (cart.cartItems.length === 0) {
        showToastMessage('Cart is empty', 'error');
        return;
      }

      // Extract unique categories/tags from cart items
      const tags = Array.from(new Set(
        cart.cartItems
          .map(item => item.category)
          .filter(Boolean)
      )) as string[];

      // Extract service names for provider matching (IMPORTANT)
      const serviceNames = Array.from(new Set(
        cart.cartItems
          .map(item => item.name)
          .filter(Boolean)
      )) as string[];

      // Transform cart items to include scheduling and payment for each item
      const orderItems: OrderItem[] = cart.cartItems.map(item => {
        // Calculate item total (price * quantity + addons)
        let itemSubtotal = item.price * item.quantity;
        if (item.addons && item.addons.length > 0) {
          const addonTotal = item.addons.reduce((sum, addon) => sum + addon.price, 0);
          itemSubtotal += addonTotal * item.quantity;
        }

        // Apply promo discount proportionally if applicable
        let itemTotal = itemSubtotal;
        if (appliedPromo && discount > 0) {
          const discountRatio = discount / subtotal;
          const itemDiscount = itemSubtotal * discountRatio;
          itemTotal = itemSubtotal - itemDiscount;
        }

        return {
          ...item,
          scheduling: {
            date: item.date,
            time: item.time,
          },
          payment: {
            method: formData.paymentMethod,
            status: 'pending',
            subtotal: itemSubtotal,
            total: itemTotal,
          },
          status: 'placed',
        };
      });

      // Create order data with unified structure
      const orderData: OrderData = {
        userId: auth.user.uid,
        userInfo: auth.user.uid, // For legacy compatibility
        items: orderItems,
        serviceNames, // For provider matching
        ...(tags.length > 0 && { tags }), // Only include tags field if tags exist
        address: {
          fullName: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          houseNumber: formData.houseNumber || '',
          landmark: formData.landmark || '',
          streetAddress: formData.streetAddress || '',
          city: formData.city || '',
          state: formData.state || '',
          country: formData.country || 'India',
          pincode: formData.pincode || '',
          address: formData.address || '',
        },
        // Scheduling - first item's schedule as primary
        scheduling: {
          date: orderItems[0]?.scheduling?.date || '',
          time: orderItems[0]?.scheduling?.time || '',
        },
        createdAt: new Date().toISOString(),
        // Store payment summary - unified structure
        payment: {
          method: formData.paymentMethod,
          status: 'pending', // Will be 'completed' after PhonePe success, stays 'pending' for PAS
          amount: total,
          subtotal: subtotal,
          total: total,
          transactionId: null,
          paidAt: null,
          ...(appliedPromo && { promoCode: appliedPromo.code, discount })
        } as any,
        // Additional services tracking (empty initially, populated by provider during service)
        additionalServices: [],
        // Price breakdown
        subtotal: subtotal,
        discount: discount,
        total: total,
        additionalTotal: 0,
        grandTotal: total,
        amountPaid: formData.paymentMethod === 'pas' ? 0 : total, // PAS: nothing paid yet
        amountDue: formData.paymentMethod === 'pas' ? total : 0, // PAS: full amount due
        // Explicit total fields for compatibility with other views
        totalAmount: total,
        finalAmount: total,
        // Provider fields (will be set when provider accepts)
        providerId: null,
        providerName: null,
        providerPhone: null,
        // Timestamps
        confirmedAt: null,
        startedAt: null,
        completedAt: null,
        cancelledAt: null,
      };

      // Log order data before saving
      console.log('Order Data to be saved:', JSON.stringify(orderData, null, 2));

      // Check database connection
      if (!db) {
        throw new Error('Database connection not initialized');
      }

      // Save order to Firestore
      console.log('Saving order to Firestore...');
      const orderId = await generateSequentialOrderId();
      console.log('Generated sequential order ID:', orderId);
      const orderRef = doc(db, 'orders', orderId);
      
      await setDoc(orderRef, orderData);

      // Create service tasks for providers (NEW ARCHITECTURE)
      try {
        console.log('Creating service tasks for providers...');
        const serviceTasks = await createServiceTasksFromOrder(
          orderId,
          auth.user.uid,
          formData.fullName,
          cart.cartItems,
          {
            fullName: formData.fullName,
            phone: formData.phone,
            email: formData.email,
            houseNumber: formData.houseNumber || '',
            streetAddress: formData.streetAddress || '',
            city: formData.city || '',
            state: formData.state || '',
            pincode: formData.pincode || '',
            landmark: formData.landmark || '',
          },
          total
        );
        console.log(`Created ${serviceTasks.length} service tasks`);
        
        // Log grouped tasks for debugging
        if (serviceTasks.length > 1) {
          const hasRelated = serviceTasks.some(t => t.relatedTaskIds && t.relatedTaskIds.length > 0);
          if (hasRelated) {
            console.log('🔗 This order contains related tasks that can be grouped for providers');
          }
        }
      } catch (taskError) {
        console.error('Error creating service tasks:', taskError);
        // Don't fail the order if task creation fails
        // Tasks can be created later via admin panel if needed
      }

      // If promo code was used, increment its usage count
      if (appliedPromo && validPromoCode) {
        try {
          const promoQuery = query(
            collection(db, 'promoCodes'),
            where('code', '==', appliedPromo.code),
            limit(1)
          );
          const promoSnapshot = await getDocs(promoQuery);
          if (!promoSnapshot.empty) {
            const promoDoc = promoSnapshot.docs[0];
            await updateDoc(doc(db, 'promoCodes', promoDoc.id), {
              usedCount: increment(1)
            });
          }
        } catch (promoError) {
          console.error('Error updating promo usage count:', promoError);
          // Don't fail the order if promo count update fails
        }
      }
      console.log('Order saved successfully with ID:', orderId);

      // Update user profile with basic info only (use setDoc with merge to avoid errors)
      console.log('Updating user profile...');
      try {
        const userRef = doc(db, 'users', auth.user.uid);
        await setDoc(userRef, {
          fullName: formData.fullName,
          phoneNumber: formData.phone,
          email: formData.email,
          updatedAt: new Date().toISOString(),
        }, { merge: true });
        console.log('User profile updated successfully');
      } catch (userUpdateError) {
        console.error('Error updating user profile (non-critical):', userUpdateError);
        // Don't fail the order if user profile update fails
      }

      // Send order confirmation emails
      console.log('Sending order confirmation emails...');
      try {
        await sendOrderEmails({
          orderId,
          customerName: formData.fullName,
          customerEmail: formData.email,
          customerPhone: formData.phone,
          items: orderData.items, // Use orderItems which include scheduling
          address: orderData.address,
          payment: {
            method: formData.paymentMethod,
            subtotal,
            total,
          },
        });
        console.log('Order confirmation emails sent successfully');
      } catch (emailError) {
        console.error('Failed to send order emails:', emailError);
        // Don't fail the order if email fails, just log it
      }

      // Store order data in localStorage for order confirmation page
      localStorage.setItem('lastOrderId', orderId);
      localStorage.setItem('lastOrderData', JSON.stringify(orderData));

      // Track purchase completion
      trackPurchase({
        orderId,
        value: total,
        items: cart.cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          category: item.category
        })),
        paymentMethod: formData.paymentMethod,
        promoCode: appliedPromo?.code,
        discount
      });

      // Clear cart
      cart.clearCart();

      // Show success
      setOrderPlaced(true);
      showToastMessage('Order placed successfully!', 'success');

      // Redirect to order confirmation page
      setTimeout(() => {
        navigate(`/order-confirmation/${orderId}`);
      }, 1500);
    } catch (error: any) {
      console.error('Error placing order:', error);
      console.error('Error details:', {
        message: error?.message,
        code: error?.code,
        stack: error?.stack
      });
      
      // Show more specific error message
      const errorMessage = error?.message || 'Failed to place order. Please try again.';
      showToastMessage(`Order failed: ${errorMessage}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show order confirmation
  if (orderPlaced) {
    return (
      <OrderConfirmation
        formData={formData}
        cartItems={cart.cartItems}
        calculateTotals={() => calculateTotals(cart.cartItems)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8 lg:py-12">
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Progress Steps */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-center">
            {[1, 2, 3].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div
                  className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full text-sm md:text-base ${
                    step >= stepNumber
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  {stepNumber}
                </div>
                {stepNumber < 3 && (
                  <div
                    className={`w-12 sm:w-16 md:w-24 h-1 ${
                      step > stepNumber ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-2 space-x-8 sm:space-x-12 md:space-x-20">
            <span className="text-xs md:text-sm font-medium">Address</span>
            <span className="text-xs md:text-sm font-medium">Date & Time</span>
            <span className="text-xs md:text-sm font-medium">Payment</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {/* Main Form Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 lg:p-8"
            >
              {/* Step 1: Address Details */}
              {step === 1 && (
                <div className="space-y-4 md:space-y-6">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
                    Service Address
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          validationErrors.fullName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter your full name"
                      />
                      {validationErrors.fullName && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.fullName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          validationErrors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter your phone number"
                      />
                      {validationErrors.phone && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          validationErrors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter your email"
                      />
                      {validationErrors.email && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        House/Flat Number *
                      </label>
                      <input
                        type="text"
                        name="houseNumber"
                        value={formData.houseNumber}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          validationErrors.houseNumber ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="House/Flat No."
                      />
                      {validationErrors.houseNumber && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.houseNumber}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Landmark *
                      </label>
                      <input
                        type="text"
                        name="landmark"
                        value={formData.landmark}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Nearby landmark"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Street Address *
                      </label>
                      <input
                        type="text"
                        name="streetAddress"
                        value={formData.streetAddress}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          validationErrors.streetAddress ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Street address"
                      />
                      {validationErrors.streetAddress && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.streetAddress}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          validationErrors.city ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="City"
                      />
                      {validationErrors.city && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.city}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State 
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          validationErrors.state ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="State"
                      />
                      {validationErrors.state && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.state}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pincode *
                      </label>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          validationErrors.pincode ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Pincode"
                      />
                      {validationErrors.pincode && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.pincode}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Country"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end mt-6">
                    <button
                      onClick={handleNextStep}
                      className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Date & Time Selection - Per Service */}
              {step === 2 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Confirm Schedule for Each Service
                  </h2>

                  {/* Show each cart item's scheduled time */}
                  {cart.cartItems.map((item, index) => {
                    const isPackage = item.category?.toLowerCase().includes('travel') || 
                                     item.category?.toLowerCase().includes('tour') ||
                                     item.category?.toLowerCase() === 'package' || 
                                     item.name?.toLowerCase().includes('package') || 
                                     item.name?.toLowerCase().includes('tour') ||
                                     item.name?.toLowerCase().includes('valley') ||
                                     item.name?.toLowerCase().includes('trip');
                    
                    return (
                      <div key={item.cartItemId || item.id} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-600 font-bold">{index + 1}</span>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{item.name}</h3>
                            <p className="text-sm text-gray-600 mt-1">⏱️ {item.duration}</p>
                            {item.category && (
                              <span className="inline-block mt-1 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                                {item.category}
                              </span>
                            )}
                            {item.addons && item.addons.length > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                + {item.addons.length} addon{item.addons.length > 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className={`grid grid-cols-1 ${isPackage ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-4`}>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {isPackage ? 'Start Date *' : 'Service Date *'}
                            </label>
                            <div className="relative">
                              <input
                                type="date"
                                value={item.date}
                                disabled
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 font-medium"
                              />
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                <Calendar className="w-4 h-4 text-gray-400" />
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(item.date).toLocaleDateString('en-IN', { 
                                weekday: 'long', 
                                day: 'numeric', 
                                month: 'long',
                                year: 'numeric'
                              })}
                            </p>
                          </div>

                          {!isPackage && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Service Time *
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={item.time}
                                  disabled
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 font-medium"
                                />
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                  <Clock className="w-4 h-4 text-gray-400" />
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Scheduled appointment time
                              </p>
                            </div>
                          )}
                        </div>

                        {isPackage && (
                          <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <Calendar className="w-4 h-4 text-purple-600 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-purple-900">
                                  Multi-day Package Service
                                </p>
                                <p className="text-xs text-purple-700 mt-1">
                                  This package spans multiple days. The service will begin on the selected start date.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {item.quantity > 1 && (
                          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                            👥 Service for {item.quantity} {item.quantity === 1 ? 'person' : 'people'}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <div className="flex items-start">
                      <Clock className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          Service Scheduling Information
                        </p>
                        <p className="text-sm text-blue-700 mt-1">
                          Single-day services will be performed at the scheduled time. Multi-day packages will begin on the selected start date. Our team will contact you with detailed itinerary.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between mt-6">
                    <button
                      onClick={handlePrevStep}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleNextStep}
                      className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Continue to Payment
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Payment */}
              {step === 3 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Payment Details
                  </h2>

                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'phonepe' }))}
                        className={`p-4 border-2 rounded-lg flex flex-col items-center justify-center ${
                          formData.paymentMethod === 'phonepe'
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-300 hover:border-purple-300'
                        }`}
                      >
                        <Smartphone className="w-6 h-6 mb-2 text-purple-600" />
                        <span className="font-semibold text-sm">PhonePe</span>
                        <span className="text-xs text-gray-500 mt-1">Recommended</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'pas' }))}
                        className={`p-4 border-2 rounded-lg flex flex-col items-center justify-center ${
                          formData.paymentMethod === 'pas'
                            ? 'border-green-600 bg-green-50'
                            : 'border-gray-300 hover:border-green-300'
                        }`}
                      >
                        <CheckCircle className="w-6 h-6 mb-2" />
                        <span className="font-semibold text-sm">Pay After Service</span>
                      </button>
                    </div>
                  </div>

                  {formData.paymentMethod === 'phonepe' && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
                      <div className="flex items-start">
                        <Smartphone className="w-5 h-5 text-purple-600 mt-0.5 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-purple-900">PhonePe Payment Gateway</p>
                          <p className="text-sm text-purple-800 mt-1">
                            You will be redirected to PhonePe to complete the payment securely. Pay using UPI, Cards, Wallets, or Net Banking.
                          </p>
                          <div className="flex items-center mt-2 text-xs text-purple-700">
                            <Shield className="w-4 h-4 mr-1" />
                            <span>100% Secure & Encrypted Payment</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.paymentMethod === 'pas' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                      <p className="text-sm text-green-900">
                        Pay with cash when our professional arrives for the service.
                      </p>
                    </div>
                  )}

                  <div className="flex justify-between mt-6">
                    <button
                      onClick={handlePrevStep}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={handlePlaceOrder}
                      disabled={isSaving || isProcessingPayment}
                      className={`px-6 py-3 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${
                        formData.paymentMethod === 'phonepe' 
                          ? 'bg-purple-600 hover:bg-purple-700' 
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {isSaving || isProcessingPayment ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          {isProcessingPayment ? 'Redirecting to PhonePe...' : 'Processing...'}
                        </>
                      ) : (
                        <>
                          {formData.paymentMethod === 'phonepe' ? (
                            <>
                              <Smartphone className="w-5 h-5 mr-2" />
                              Proceed to PhonePe Payment
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-5 h-5 mr-2" />
                              Place Order
                            </>
                          )}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Order Summary Section */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-6 sticky top-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h3>
              
              <div className="space-y-3 mb-6">
                {cart.cartItems.map((item) => (
                  <div key={item.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">{item.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">₹{item.price.toLocaleString()} × {item.quantity}</p>
                        </div>
                        <button
                          onClick={() => cart.removeFromCart(item.id)}
                          className="text-red-500 hover:text-red-700 transition-colors p-1 hover:bg-red-50 rounded"
                          title="Remove item"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      
                      {item.addons && item.addons.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                          {item.addons.map((addon) => (
                            <div key={addon.id} className="flex justify-between text-xs">
                              <span className="text-gray-600">+ {addon.name}</span>
                              <span className="text-gray-600">₹{addon.price.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between items-center">
                        <span className="text-xs font-medium text-gray-700">Item Total</span>
                        <span className="text-sm font-semibold text-gray-900">
                          ₹{((item.price + (item.addons?.reduce((sum, addon) => sum + addon.price, 0) || 0)) * item.quantity).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Promo Code Section - Show at Step 3 */}
              {step === 3 && (
                <div className="mb-6 p-4 md:p-5 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border-2 border-purple-200">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm md:text-base font-semibold text-gray-900 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Have a promo code?
                    </p>
                    {!appliedPromo && (
                      <span className="text-xs text-purple-600 font-medium hidden md:inline">Save more!</span>
                    )}
                  </div>
                  
                  {appliedPromo ? (
                    <div className="flex items-center justify-between p-3 md:p-4 bg-white rounded-lg border-2 border-green-400 shadow-sm">
                      <div className="flex items-center space-x-3">
                        <div className="bg-green-100 p-2 rounded-full">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm md:text-base font-bold text-green-700">{appliedPromo.code}</p>
                          <p className="text-xs text-green-600">Discount applied successfully!</p>
                        </div>
                      </div>
                      <button
                        onClick={handleRemovePromo}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 transition-all p-2 rounded-lg"
                        title="Remove promo code"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <div className="relative">
                          <input
                            type="text"
                            value={promoCode}
                            onChange={(e) => {
                              setPromoCode(e.target.value.toUpperCase());
                              setPromoError('');
                            }}
                            placeholder="Enter promo code"
                            className="w-full px-4 py-2.5 border-2 border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white placeholder-gray-400"
                          />
                        </div>
                        <button
                          onClick={handleApplyPromo}
                          disabled={!promoCode.trim()}
                          className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                        >
                          Apply Code
                        </button>
                      </div>
                      {promoError && (
                        <div className="flex items-center space-x-2 p-2 bg-red-50 rounded-lg">
                          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                          <p className="text-xs md:text-sm text-red-600">{promoError}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal</span>
                  <span>₹{subtotal.toLocaleString()}</span>
                </div>
                
                {appliedPromo && discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Discount ({appliedPromo.code})
                    </span>
                    <span>-₹{discount.toLocaleString()}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-lg font-bold text-gray-900 pt-3 border-t-2 border-gray-300">
                  <span>Total</span>
                  <span className="text-primary-600">₹{total.toLocaleString()}</span>
                </div>
                
                {appliedPromo && discount > 0 && (
                  <p className="text-xs text-center text-green-600 font-medium">
                    You saved ₹{discount.toLocaleString()}!
                  </p>
                )}
              </div>

              {/* Address Display - Show after Step 1 */}
              {step >= 2 && formData.fullName && formData.city && (
                <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-semibold text-green-900 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Service Address
                  </p>
                  <div className="space-y-1 text-sm text-green-800">
                    <p className="font-medium">{formData.fullName}</p>
                    <p>{formData.houseNumber}, {formData.streetAddress}</p>
                    {formData.landmark && <p>Near: {formData.landmark}</p>}
                    <p>{formData.city}, {formData.state} - {formData.pincode}</p>
                    <p className="text-xs mt-2">📞 {formData.phone}</p>
                  </div>
                </div>
              )}

              {/* Date & Time Display - Show after Step 2 */}
              {step >= 2 && cart.cartItems.length > 0 && (
                <div className="mt-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
                  <p className="text-sm font-semibold text-primary-900 mb-3">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Scheduled Services
                  </p>
                  <div className="space-y-3">
                    {cart.cartItems.map((item, index) => {
                      const isPackage = item.category?.toLowerCase() === 'package' || item.duration?.includes('day') || item.duration?.includes('night');
                      
                      return (
                        <div key={item.cartItemId || item.id} className="bg-white rounded-lg p-3 border border-primary-200">
                          <div className="flex items-start gap-2">
                            <div className="flex-shrink-0 w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
                              <span className="text-primary-600 text-xs font-bold">{index + 1}</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                <span className="flex items-center gap-1 text-xs text-primary-800">
                                  <Calendar className="w-3 h-3" />
                                  {isPackage ? 'Starts: ' : ''}{new Date(item.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                </span>
                                {!isPackage && item.time && (
                                  <span className="flex items-center gap-1 text-xs text-primary-800">
                                    <Clock className="w-3 h-3" />
                                    {item.time}
                                  </span>
                                )}
                                {isPackage && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                    Multi-day
                                  </span>
                                )}
                              </div>
                              {item.quantity > 1 && (
                                <p className="text-xs text-gray-600 mt-1">
                                  👥 {item.quantity} {item.quantity === 1 ? 'person' : 'people'}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-6 space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <Shield className="w-4 h-4 mr-2 text-green-600" />
                  <span>Secure checkout</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                  <span>100% satisfaction guaranteed</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
